import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { fetchContacts } from './counterpartyApi'
import type { ContactCard, Participant, ParticipantRole } from './contactApi'
import {
  PARTICIPANT_ROLES,
  addParticipant,
  createContact,
  fetchContactCard,
  fetchParticipants,
  removeParticipant,
} from './contactApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Контакты и участники сделки на карточках (тикет #98).
 *
 * Отказ сервера показывается его словами: «контакт не относится к контрагенту сделки»
 * и «участвует в открытых сделках» — это правила, которые пользователь должен прочитать,
 * а не общая «ошибка сохранения».
 */

function messageOf(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.isForbidden ? fallback : error.message
  return fallback
}

// ------------------------------------------------------------------ новый контакт

/**
 * Форма нового контакта у контрагента.
 *
 * Живёт на карточке контрагента, а не в списке контактов: контакт заводится только у
 * контрагента, и выбор его из выпадающего списка всех компаний был бы и неудобным, и
 * способом подсадить человека в чужую — сервер это всё равно отвергнет.
 */
export function NewContactForm({ counterpartyId }: { counterpartyId: string }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ lastName: '', firstName: '', jobTitle: '', phone: '', email: '', primary: false })
  const [notice, setNotice] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      createContact({
        counterpartyId,
        lastName: draft.lastName,
        firstName: draft.firstName || undefined,
        jobTitle: draft.jobTitle || undefined,
        phone: draft.phone || undefined,
        email: draft.email || undefined,
        primary: draft.primary,
      }),
    onSuccess: () => {
      setNotice(null)
      setOpen(false)
      setDraft({ lastName: '', firstName: '', jobTitle: '', phone: '', email: '', primary: false })
      void queryClient.invalidateQueries({ queryKey: ['counterparty-contacts', counterpartyId] })
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.contact'] })
    },
    onError: (error) => setNotice(messageOf(error, t('app.error'))),
  })

  if (!can(user, 'sales.contact', 'create')) return null
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}>
        {t('contact.add')}
      </button>
    )
  }
  return (
    <form
      className="card-form"
      aria-label={t('contact.add')}
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate()
      }}
    >
      <label>
        {t('column.last_name')}
        <input required value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
      </label>
      <label>
        {t('column.first_name')}
        <input value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
      </label>
      <label>
        {t('column.job_title')}
        <input value={draft.jobTitle} onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })} />
      </label>
      <label>
        {t('column.phone')}
        <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
      </label>
      <label>
        {t('column.email')}
        <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
      </label>
      <label>
        <input type="checkbox" checked={draft.primary} onChange={(e) => setDraft({ ...draft, primary: e.target.checked })} />
        {t('card.cp.contact.primary')}
      </label>
      {notice && <p role="alert">{notice}</p>}
      <div className="report-actions">
        <button type="submit" disabled={create.isPending || !draft.lastName.trim()}>
          {t('contact.create')}
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          {t('admin.cancel')}
        </button>
      </div>
    </form>
  )
}

// ------------------------------------------------------------------ сделки контакта

function ContactDeals({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['contact-card', recordId],
    queryFn: ({ signal }) => fetchContactCard(recordId, signal),
    retry: false,
  })
  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) return <p role="alert">{messageOf(query.error, t('access.denied'))}</p>
  const card = query.data as ContactCard
  return (
    <div className="card-gap">
      {card.counterparty && (
        <p>
          {t('contact.counterparty')}:{' '}
          <Link to={`/counterparties/${card.counterparty.id}`}>{card.counterparty.name}</Link>
        </p>
      )}
      {card.deals.length === 0 ? (
        <p className="muted">{t('contact.deals.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('contact.deals.title')}>
          <thead>
            <tr>
              <th>{t('column.number')}</th>
              <th>{t('column.title')}</th>
              <th>{t('column.status')}</th>
              <th>{t('participant.role')}</th>
            </tr>
          </thead>
          <tbody>
            {card.deals.map((deal) => (
              <tr key={deal.dealId}>
                <td>
                  <Link to={`/deals/${deal.dealId}`}>{deal.number}</Link>
                </td>
                <td>{deal.title}</td>
                <td>{t(`deal.status.${deal.status}`)}</td>
                <td>
                  {t(`participant.role.${deal.role}`)}
                  {deal.primary && <span className="badge"> {t('participant.primary')}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function contactDealsTab(): CardTab {
  return {
    id: 'deals',
    labelKey: 'contact.deals.title',
    render: (context: CardTabContext) => <ContactDeals recordId={context.recordId} />,
  }
}

// ------------------------------------------------------------------ участники сделки

function DealParticipants({ dealId, counterpartyId }: { dealId: string; counterpartyId: string | null }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [contactId, setContactId] = useState('')
  const [role, setRole] = useState<ParticipantRole>('decision_maker')
  const [primary, setPrimary] = useState(false)
  const mayEdit = can(user, 'sales.deal', 'update')

  const participants = useQuery({
    queryKey: ['deal-participants', dealId],
    queryFn: ({ signal }) => fetchParticipants(dealId, signal),
    retry: false,
  })
  // Кандидаты — люди контрагента сделки: только их сервер и примет.
  const candidates = useQuery({
    queryKey: ['counterparty-contacts', counterpartyId],
    queryFn: ({ signal }) => fetchContacts(counterpartyId as string, signal),
    enabled: mayEdit && Boolean(counterpartyId),
    retry: false,
  })

  const refresh = (list: Participant[]) => {
    setNotice(null)
    queryClient.setQueryData(['deal-participants', dealId], list)
  }
  const add = useMutation({
    mutationFn: () => addParticipant(dealId, { contactId, role, primary }),
    onSuccess: (list) => {
      setContactId('')
      setPrimary(false)
      refresh(list)
    },
    onError: (error) => setNotice(messageOf(error, t('app.error'))),
  })
  const remove = useMutation({
    mutationFn: (id: string) => removeParticipant(dealId, id),
    onSuccess: refresh,
    onError: (error) => setNotice(messageOf(error, t('app.error'))),
  })

  if (participants.isLoading) return <p>{t('app.loading')}</p>
  if (participants.error) return <p role="alert">{messageOf(participants.error, t('access.denied'))}</p>
  const list = participants.data ?? []

  return (
    <div className="card-gap">
      {list.length === 0 ? (
        <p className="muted">{t('participant.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('participant.title')}>
          <thead>
            <tr>
              <th>{t('card.cp.contact.name')}</th>
              <th>{t('participant.role')}</th>
              <th>{t('column.phone')}</th>
              <th>{t('column.email')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((person) => (
              <tr key={person.contactId}>
                <td>
                  {/* Удалённый после закрытия сделки контакт остаётся в её истории —
                      помеченным, а не исчезнувшим. */}
                  {person.deleted ? (
                    <span className="muted">{t('participant.deleted')}</span>
                  ) : (
                    <Link to={`/contacts/${person.contactId}`}>{person.fullName}</Link>
                  )}
                  {person.primary && <span className="badge"> {t('participant.primary')}</span>}
                </td>
                <td>{t(`participant.role.${person.role}`)}</td>
                <td>{person.phone ?? '—'}</td>
                <td>{person.email ?? '—'}</td>
                <td>
                  {mayEdit && (
                    <button
                      type="button"
                      aria-label={`${t('participant.remove')} ${person.fullName ?? ''}`}
                      onClick={() => remove.mutate(person.contactId)}
                    >
                      {t('participant.remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {notice && <p role="alert">{notice}</p>}
      {mayEdit && counterpartyId && (
        <form
          className="report-actions"
          aria-label={t('participant.add')}
          onSubmit={(event) => {
            event.preventDefault()
            add.mutate()
          }}
        >
          <select aria-label={t('participant.contact')} value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">{t('participant.chooseContact')}</option>
            {(candidates.data ?? []).map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.fullName}
              </option>
            ))}
          </select>
          <select aria-label={t('participant.role')} value={role} onChange={(e) => setRole(e.target.value as ParticipantRole)}>
            {PARTICIPANT_ROLES.map((code) => (
              <option key={code} value={code}>
                {t(`participant.role.${code}`)}
              </option>
            ))}
          </select>
          <label>
            <input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
            {t('participant.primary')}
          </label>
          <button type="submit" disabled={!contactId || add.isPending}>
            {t('participant.add')}
          </button>
        </form>
      )}
      {mayEdit && !counterpartyId && <p className="muted">{t('participant.noCounterparty')}</p>}
    </div>
  )
}

export function participantsTab(): CardTab {
  return {
    id: 'participants',
    labelKey: 'participant.title',
    render: (context: CardTabContext) => (
      <DealParticipants
        dealId={context.recordId}
        counterpartyId={(context.record?.values.counterparty_id as string | null | undefined) ?? null}
      />
    ),
  }
}
